import { List, ListItem, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { BannerInfo, BannerInfoContainer, TypoContainer } from "./BannerInfoContainer.styles";
import { Progress } from "./Progress";
import { useAttributeTabStore } from "../../stores/attributeTabStore";
import type { RunsticResponse } from "../../types";
import { checkCustomLogicFe, getEnvironmentVariables } from "../../utils/general";
import { CircularProgress } from "../CircularProgress/CircularProgress.component";

//import { ATTRIBUTE_TRANSLATION_MAP } from "../AgendaWidget/utils";

export type ICourse = {
  id: string;
  nameCourse: string;
  completed: number;
  total: number;
  mandatory: boolean;
}
type NewProgress = {
    attributeName: string,
    attributeId: number | string,
    los: number,
    completedLos: number,
    showCircle?: boolean,
};

export const ProgressContainer = ({ userProgress, translatedAttribute }: { userProgress: RunsticResponse; translatedAttribute: boolean }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { customLogicFe } = getEnvironmentVariables();
  const [hoverCourse, setHoverCourse] = useState<string | number | null>(null);
  const [progressList, setProgressList] = useState<NewProgress[]>([]); //lista degli attributi con relativo cerchio di progressione
  const [additionalItems, setAdditionalItems] = useState<NewProgress[]>([]);

  const { selectedTab, setSelectedTab } = useAttributeTabStore();

  useEffect(() => {
    if(userProgress){
      //handleProgressList();
      const attributes = userProgress.attributes?.map((singleAttribute:NewProgress) => {
        return  {
          attributeId:singleAttribute.attributeId,
          attributeName:singleAttribute.attributeName,
          completedLos:singleAttribute.completedLos,
          los:singleAttribute.los,
          showCircle: true
        };
      }) || [];

      const additionalCategories: NewProgress[] = [];

      // Riordino arrayResult: [attributo posizione-1 admin (solo se presente), mandatory, attributi rimanenti]
      const arrayResult: NewProgress[] = [];

      // Aggiungo il primo attribute solo se è quello con posizione 1 in admin (translatedAttribute)
      if (translatedAttribute && attributes.length > 0) {
        arrayResult.push(attributes[0]);
      }

      // Aggiungo mandatory
      if(userProgress.mandatory && userProgress.mandatory.los > 0 && !checkCustomLogicFe({ envVariable: customLogicFe, strValueToCheck: "hideMandatoryForYou" })) {
        arrayResult.push({
          attributeName: "mandatory",
          attributeId: "mandatory",
          los: userProgress.mandatory.los,
          completedLos: userProgress.mandatory.completedLos,
          showCircle: false
        });
      }

      // Aggiungo i rimanenti attributes: se translatedAttribute, dal secondo in poi; altrimenti tutti
      if (translatedAttribute) {
        if (attributes.length > 1) {
          arrayResult.push(...attributes.slice(1));
        }
      } else {
        arrayResult.push(...attributes);
      }

      setProgressList(arrayResult); //set list after mandatories check

      // Suggested by Manager
      if(userProgress.suggestedByManager && userProgress.suggestedByManager.los > 0) {
        additionalCategories.push({
          attributeName: "MANAGER_SUGGESTED",
          attributeId: "suggestedByManager",
          los: userProgress.suggestedByManager.los,
          completedLos: userProgress.suggestedByManager.completedLos,
          showCircle: true
        });
      }

      // Suggested by HR
      if(userProgress.suggestedByHR && userProgress.suggestedByHR.los > 0) {
        additionalCategories.push({
          attributeName: "HR_SUGGESTED",
          attributeId: "suggestedByHR",
          los: userProgress.suggestedByHR.los,
          completedLos: userProgress.suggestedByHR.completedLos,
          showCircle: false
        });
      }

      // Suggested by AI
      if(userProgress.suggestedByAI && userProgress.suggestedByAI.los > 0) {
        additionalCategories.push({
          attributeName: "AI_SUGGESTED",
          attributeId: "suggestedByAI",
          los: userProgress.suggestedByAI.los,
          completedLos: userProgress.suggestedByAI.completedLos,
          showCircle: false
        });
      }

      setAdditionalItems(additionalCategories);
    }

  }, [userProgress, translatedAttribute]);

  const calculateProgressPercentage = (progress: number, total: number) => {
    if(!progress || !total) return 0;
    if(total === 0) return 0;

    return (progress / total) * 100;
  };

  const calculateRadius = (index: number) => {
    let radiusMax = 38.4;
    let radius = radiusMax - (index * 6.4);
    let r = radius / 16; //pixel into rem
    let radiusInRem = (r + "rem").toString(); //rem as string
    return { r: radiusInRem, radius };
  };

  const textColorCourse = (mandatory: boolean) =>
    mandatory ? theme.customColors.textWarning : theme.customColors.systemSecondary01;

  const isHover = (courseTagId: string | number | null) => courseTagId === hoverCourse || selectedTab === courseTagId;
  
  const handleLabel =(label:number | null)=> label ?? "0";

  const isNewLayout = progressList.length >= 6;

  return (
    <div
      aria-label={ t("your_progress") }
      id="progress-container"
    >
      { !isNewLayout && (
        <svg viewBox="-15 -45 30 90" height="13.5rem" width={ "100%" } aria-hidden="true">
          { progressList?.map((attribute: NewProgress, index: number) => {
            return (
              <Progress
                key={ attribute.attributeName + "-progression-circular" }
                onHover={ (id) => setHoverCourse(id) }
                progress={
                  calculateProgressPercentage(
                    //@ts-ignore
                    attribute?.completedLos, attribute?.los)
                }
                { ...calculateRadius(index) }
                //@ts-ignore
                courseInfo={ attribute }
                courseId={ attribute.attributeId }
                courseTag={ attribute.attributeName }
                isHover={ isHover }
              />
            );
          }) }
        </svg>
      ) }

      <List disablePadding>
        { progressList?.map((attribute:NewProgress) => {
          const isMandatory = attribute.attributeName === "mandatory";
          const courseId = `course-${attribute.attributeName}`;
          const courseCompletion = `course-${attribute.attributeName}-completion`;
          const progress = calculateProgressPercentage(
            //@ts-ignore
            attribute?.completedLos, attribute?.los
          );
          return (
            <ListItem
              disablePadding
              aria-roledescription="course name and your progression"
              tabIndex={ 0 }
              key={ attribute.attributeName }
            >
              <BannerInfoContainer
                $isHover={ isHover(attribute.attributeId) || selectedTab === attribute.attributeId }
                $isNewLayout={ isNewLayout }
                onMouseOver={ () =>  setHoverCourse(attribute.attributeId) }
                onMouseLeave={ () => setHoverCourse(null) }
                onClick={ () => setSelectedTab(attribute.attributeId.toString()) }
              >
                <BannerInfo
                  $isNewLayout={ isNewLayout }
                  $mandatory={ isMandatory }
                >
                  <TypoContainer
                    $isNewLayout={ isNewLayout }>
                    <Typography fontSize="0.75rem" fontWeight={ 700 } color={ textColorCourse(isMandatory) }>
                      <span id={ courseId }>
                        { isMandatory ? t(attribute.attributeName) : attribute.attributeName }
                      </span>
                    </Typography>
                    <Typography fontSize="0.75rem" color={ theme.customColors.exploreCardText }>
                      <span id={ courseCompletion }>
                        {
                        //@ts-ignore
                          handleLabel(attribute.completedLos)
                        + "/" +
                        //@ts-ignore
                        handleLabel(attribute.los) + " " + t("filters_completed")
                        }
                      </span>
                    </Typography>
                  </TypoContainer>
                </BannerInfo>
                { isNewLayout && (
                  <CircularProgress
                    circleColor={ isMandatory ? theme.customColors.textWarning : theme.customColors.systemSecondary01 }
                    hidePercentage={ true }
                    percentageOfCompletion={ `${Math.round(progress)}%` }
                    roundedStroke={ true }
                    thickness={ 6 }
                  />
                ) }
              </BannerInfoContainer>
            </ListItem>
          );
        }) }

        { /*mandatory, suggestedByManager, suggestedByHr, suggestedByAI*/ }
        { additionalItems?.map((item: NewProgress) => {
          const isMandatory = item.attributeName === "MANDATORY";
          const courseId = `course-${item.attributeName}`;
          const courseCompletion = `course-${item.attributeName}-completion`;
          const progress = calculateProgressPercentage(item.completedLos, item.los);
          return (
            <ListItem
              disablePadding
              aria-roledescription="course name and your progression"
              tabIndex={ 0 }
              key={ item.attributeName }
            >
              <BannerInfoContainer
                $isNewLayout={ isNewLayout }
              >
                <BannerInfo
                  $isNewLayout={ isNewLayout }
                  $mandatory={ isMandatory }
                >
                  <TypoContainer
                    $isNewLayout={ isNewLayout }>
                    <Typography fontSize="0.75rem" fontWeight={ 700 } color={ textColorCourse(isMandatory) }>
                      <span id={ courseId }>
                        { t(item.attributeName) }
                      </span>
                    </Typography>
                    <Typography fontSize="0.75rem" color={ theme.customColors.exploreCardText }>
                      <span id={ courseCompletion }>
                        {
                          handleLabel(item.completedLos)
                        + "/" +
                        handleLabel(item.los) + " " + t("filters_completed")
                        }
                      </span>
                    </Typography>
                  </TypoContainer>
                </BannerInfo>
                { isNewLayout && (
                  <CircularProgress
                    circleColor={ isMandatory ? theme.customColors.textWarning : theme.customColors.systemSecondary01 }
                    hidePercentage={ true }
                    percentageOfCompletion={ `${Math.round(progress)}%` }
                    roundedStroke={ true }
                    thickness={ 6 }
                  />
                ) }
              </BannerInfoContainer>
            </ListItem>
          );
        }) }
      </List>
    </div>
  );
};
